/**
 * VoiceLibraryPanel — выбор голоса озвучки как на elevenlabs.io: язык,
 * сценарий («Популярные», «Рассказы», «Соцсети», «Обучение»…) и список голосов
 * с прослушиванием. Одна панель на окно виджета (VoicePickerSheet), мобильную
 * шторку настроек (VoiceRow) и «Диалог» (там без списка — свой выбор у реплик).
 *
 * «Стандартные» — голоса ElevenLabs по умолчанию: говорят на любом языке, поэтому
 * язык у этой вкладки не фильтрует.
 */
import { useEffect, useMemo, useRef } from 'react'
import { useVoicePreview } from '../hooks/useVoicePreview'
import { sortLanguages, useVoiceFilters, useVoiceLibrary, type TtsVoice } from '../utils/ttsVoices'
import GenderIcon from './GenderIcon'

interface FiltersProps {
	filters: ReturnType<typeof useVoiceFilters>
	disabled?: boolean
}

/** Язык + сценарии — общая шапка панели и «Диалога». */
export function VoiceFilters({ filters, disabled }: FiltersProps) {
	const { options, language, setLanguage, useCase, setUseCase } = filters
	const languages = useMemo(() => sortLanguages(options.languages), [options.languages])
	const standard = useCase === 'standard'
	// Выбранный сценарий может оказаться за краем ленты (на телефоне видно 3–4) —
	// подкручиваем ленту к нему. Только по горизонтали: страницу/шторку не двигаем.
	const casesRef = useRef<HTMLDivElement>(null)
	useEffect(() => {
		const row = casesRef.current
		const active = row?.querySelector<HTMLElement>('.voice-lib__case.is-on')
		if (!row || !active) return
		const left = active.offsetLeft - row.offsetLeft
		if (left < row.scrollLeft || left + active.offsetWidth > row.scrollLeft + row.clientWidth) {
			row.scrollLeft = Math.max(0, left - 12)
		}
	}, [useCase, options.useCases])
	return (
		<div className="voice-lib__filters">
			<label className="voice-lib__lang">
				<span className="voice-lib__lang-label">Язык</span>
				<select
					value={language}
					onChange={(e) => setLanguage(e.target.value)}
					disabled={disabled || standard}
					title={standard ? 'Стандартные голоса говорят на любом языке' : undefined}
				>
					{!languages.some((l) => l.code === language) && <option value={language}>{language}</option>}
					{languages.map((l) => (
						<option key={l.code} value={l.code}>{l.label}</option>
					))}
				</select>
			</label>
			<div ref={casesRef} className="voice-lib__cases" role="tablist" aria-label="Сценарий">
				{options.useCases.map((u) => (
					<button
						key={u.key}
						type="button"
						role="tab"
						aria-selected={u.key === useCase}
						className={`voice-lib__case${u.key === useCase ? ' is-on' : ''}`}
						onClick={() => setUseCase(u.key)}
						disabled={disabled}
					>
						{u.label}
					</button>
				))}
			</div>
		</div>
	)
}

interface Props {
	currentVoiceId?: string | null
	onSelect: (voice: TtsVoice) => void
}

export default function VoiceLibraryPanel({ currentVoiceId, onSelect }: Props) {
	const filters = useVoiceFilters()
	const { voices, hasMore, loading, error, loadMore } = useVoiceLibrary(filters.language, filters.useCase)
	const { playing, toggle, stop } = useVoicePreview()

	return (
		<div className="voice-lib">
			<VoiceFilters filters={filters} />
			<div className="voice-lib__list">
				{!voices.length && (
					<div className="voice-lib__empty">
						{loading ? 'Загружаем голоса…' : error || 'В этом разделе пока нет голосов на выбранном языке'}
					</div>
				)}
				{voices.map((v) => {
					const active = v.id === currentVoiceId
					const isPlaying = playing === v.id
					return (
						<div key={v.id} className={`voice-lib__item${active ? ' is-active' : ''}`}>
							<button
								type="button"
								className={`voice-lib__play${isPlaying ? ' is-playing' : ''}`}
								onClick={() => toggle(v)}
								disabled={!v.sample}
								aria-label={isPlaying ? `Остановить ${v.label}` : `Послушать ${v.label}`}
							>
								{isPlaying ? '❚❚' : '▶'}
							</button>
							<button
								type="button"
								className="voice-lib__select"
								onClick={() => {
									stop()
									onSelect(v)
								}}
							>
								<span className="voice-lib__gender">
									<GenderIcon gender={v.gender} />
								</span>
								<span className="voice-lib__text">
									<span className="voice-lib__name">{v.label}</span>
									{v.hint && <span className="voice-lib__hint">{v.hint}</span>}
								</span>
								{active && <span className="voice-lib__check" aria-hidden="true">✓</span>}
							</button>
						</div>
					)
				})}
				{hasMore && (
					<button type="button" className="voice-lib__more" onClick={loadMore} disabled={loading}>
						{loading ? 'Загружаем…' : 'Показать ещё'}
					</button>
				)}
			</div>
		</div>
	)
}
